import React, { useState, useRef, useEffect, useCallback } from "react";

// Components
import Button from "../button";
import { useFlashcardSetup } from "./FlashcardSetupContext";
import Typography from "../Typography";
import AttachMaterials from "../AttachMaterials";

// Hooks
import { useTranslation } from "react-i18next";
import useShortCutKey from "../../hooks/useShortCutKey";

// Types
import { APIId, Note } from "../../types";

// styles
import styles from "./styles.module.scss";

export default function FlashcardUploadStep() {
  const { t } = useTranslation();
  const [error, setError] = useState<string | null>(null);
  const errorRef = useRef<HTMLElement | null>(null);

  const {
    state,
    open,
    handleFilesChange,
    handleLinksChange,
    handleSmartNoteSelect,
    handlePromptChange,
    handleSubmit,
  } = useFlashcardSetup();

  useEffect(() => {
    if (error && errorRef.current) {
      errorRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
      errorRef.current.focus();
    }
  }, [error]);

  const handleClickSubmit = useCallback(() => {
    const hasPrompt = state.prompt && state.prompt.trim().length > 0;
    const hasMaterials = state.selectedSmartNotes.length > 0 || state.files.length > 0 || state.links.length > 0;
    
    if (!hasPrompt && !hasMaterials) {
      setError(t('flashcardSetupModal.step_2.no_files_or_smart_notes'));
      return;
    }

    handleSubmit();
  }, [state.prompt, state.selectedSmartNotes.length, state.files.length, state.links.length, handleSubmit, t]);
  useShortCutKey({ key: 'Enter', onKeyPress: handleClickSubmit, active: open });

  const handleSelectSmartNote = useCallback((selected: Note[]) => {
    handleSmartNoteSelect(selected.map(note => note.id));
  }, [handleSmartNoteSelect]);

  const handleChangeUploadedFiles = useCallback((files: string[]) => {
    handleFilesChange(files);
  }, [handleFilesChange]);

  const hasPrompt = state.prompt && state.prompt.trim().length > 0;
  const hasSelectedNotes = state.selectedSmartNotes.length > 0;
  const hasFiles = state.files.length > 0;
  const hasLinks = state.links.length > 0;
  const hasAttachedMaterials = hasSelectedNotes || hasFiles || hasLinks;


  return (
    <div className="flex flex-col pl-6">
      <div className="max-h-[60vh] overflow-y-auto pr-6">
        <div className="flex flex-col w-full mx-auto">
          {error && (
            <Typography ref={errorRef} variant="span" className={`${styles.error} text-sm mb-2`}>
              {error}
            </Typography>
          )}

          {!hasPrompt && (
            <AttachMaterials
              onChangeNotes={handleSelectSmartNote}
              onChangeUploadedFiles={handleChangeUploadedFiles}
              onChangeLinks={handleLinksChange}
            />
          )}

          {!hasPrompt && !hasAttachedMaterials && (
            <div className="flex items-center justify-center mt-2 text-gray-400">
              <Typography variant="p">{t('flashcardSetupModal.step_2.or')}</Typography>
            </div>
          )}

          {(!hasAttachedMaterials || hasPrompt) && (
            <div>
              <Typography variant="label" className={`block text-lg font-semibold mb-2 ${styles.textColor}`}>
                {t('flashcardSetupModal.step_2.prompt')}
              </Typography>
              <input
                type="text"
                className="w-full border rounded-lg px-4 py-2 md:min-w-[416px] dark:bg-zeus-800 dark:text-white"
                placeholder={t('flashcardSetupModal.step_2.prompt_placeholder')}
                value={state.prompt || ""}
                onChange={e => handlePromptChange(e.target.value)}
              />
            </div>
          )}
        </div>  
      </div>

      <div className="mr-6">
        <Button
          type="primary"
          className="w-full mt-4 bg-blue-500 text-white py-3 rounded-full text-lg flex items-center justify-center gap-2 sticky bottom-0 primary-button"
          onClick={handleClickSubmit}
          disabled={state.uploading}
        >
          <span>✦</span>
          <Typography variant="p" className={`text-white cloudy:text-black text-lg font-semibold ${styles.submitButton}`}>{t('flashcardSetupModal.step_2.startGeneration')}</Typography>
        </Button>
      </div>
    </div>
  )
}
